import useHeadlessQuickNoteConvert from "../hooks/panelDashboard/useHeadlessQuickNoteConvert";
import useBackgroundCareCapture from "../hooks/useBackgroundCareCapture";
import useComposerCaptureSubmit from "../hooks/useComposerCaptureSubmit";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Container,
  Typography,
  CircularProgress,
  Fade,
  Button,
  useMediaQuery,
  Snackbar,
  Alert,
} from "@mui/material";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import CheckRoundedIcon from "@mui/icons-material/CheckRounded";
import { useLocation, useNavigate } from "react-router-dom";

// Hooks and Services
import { usePanelDashboard } from "../hooks/usePanelDashboard";

// Components
import MobileDashboardFlow from "../components/Dashboard/MobileDashboardFlow";
import DesktopDashboardWorkspace from "../components/Dashboard/DesktopDashboardWorkspace";
import QuickCheckIn from "../components/Mobile/QuickCheckIn";
import NaturalCaptureComposerShell from "../components/UI/NaturalCaptureComposerShell";
import LogMoodModal from "../components/Dashboard/LogMoodModal";
import DailyCareModal from "../components/DailyCare/DailyCareModal";
import SleepLogSheet from "../components/Sleep/SleepLogSheet";
import FoodLogSheet from "../components/Dashboard/FoodLogSheet";
import BathroomLogSheet from "../components/Dashboard/BathroomLogSheet";
import { IncidentLoggingModal, IncidentFollowUpModal } from "../components/Dashboard/Incidents";
import PatternSuggestionModal from "../components/Dashboard/PatternSuggestionModal";
import DailyHabitsModal from "../components/Dashboard/DailyHabitsModal";
import ImportReviewOverlays from "../components/Dashboard/ImportLogs/ImportReviewOverlays";
import BulkMedicationLogDialog from "./MedicalLog/components/BulkMedicationLogDialog";
import { DashboardViewProvider } from "../components/Dashboard/shared/DashboardViewContext";
import { DASHBOARD_ACTION_IDS } from "../components/Dashboard/shared/dashboardActionDefinitions";
import RenderDebugOverlay from "../components/Dashboard/shared/RenderDebugOverlay";
import { trackRenderDebug, useMountDebug } from "../utils/renderDebug";
import { logMood } from "../services/moodService";
import { getMoodOption } from "../constants/moodOptions";
import colors from "../assets/theme/colors";
import { CARE_SNAPSHOT_STEPS } from "../constants/careSnapshotSections";
import { useImportReviewUiState } from "../hooks/panelDashboard/useImportReviewUiState";
import { useImportFileReviewController } from "../hooks/panelDashboard/useImportFileReviewController";
import {
  CARE_SOURCE_TYPES,
} from "../features/careSourceProcessing";
import TimelineNoteEditSheet from "../features/timelineEdit/TimelineNoteEditSheet";
import ProfileInboundEmailCard from "../features/inboundEmail/ProfileInboundEmailCard";
import { getCalendarDateKey } from "../utils/calendarDateKey";
import {
  enableLoggingReminderNotifications,
  isLoggingReminderEnabledForChild,
  loadUserNotificationSettings,
  notificationSettingsDefaults,
  saveLoggingReminderForChild,
} from "../services/notifications/userNotificationSettings";
import {
  consumeChildSelectionRequest,
  consumeOpenCaptureDetailsRequest,
  rememberOpenCaptureDetailsRequest,
} from "./panelDashboardNavigationState";

const LOGGING_REMINDER_PROMPT_DISMISSED_KEY = "lifelog.loggingReminderPromptDismissedAt";
const LOGGING_REMINDER_PROMPT_COOLDOWN_MS = 1000 * 60 * 60 * 24 * 14;
const IMPORT_FILE_ACCEPT = '.pdf,.txt,.xlsx,.docx,application/pdf,text/plain,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.openxmlformats-officedocument.wordprocessingml.document';
const IMPORT_IMAGE_ACCEPT = 'image/*';

const getReminderErrorMessage = (status = '') => {
  if (status === 'denied') {
    return 'Notifications are blocked in this browser';
  }
  if (status === 'missing-vapid-key') {
    return 'Notification key is missing for this environment';
  }
  if (status === 'unsupported') {
    return 'This browser does not support web notifications';
  }
  if (status === 'push-token-timeout') {
    return 'Notifications took too long to start. Try again in a moment.';
  }
  if (status === 'push-token-failed') {
    return 'Could not create a notification token on this device';
  }
  if (status === 'token-register-failed') {
    return 'Could not save this device for reminders';
  }
  if (status === 'unauthenticated') {
    return 'Sign in again to turn on reminders';
  }
  return 'Could not turn on reminders on this device';
};

const PanelDashboard = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const isMobile = useMediaQuery('(max-width:1024.95px)');
  const isDesktop = !isMobile;
  const hook = usePanelDashboard({ activeChildOnly: isMobile });
  const dashboardChildren = hook.children;
  const dashboardSelectedChild = hook.selectedChild;
  const dashboardSetCurrentChildId = hook.setCurrentChildId;
  const dashboardHandleAddChild = hook.handleAddChild;
  const dashboardHandleQuickEntryComplete = hook.handleQuickEntryComplete;
  const dashboardHandleEditChild = hook.handleEditChild;
  const dashboardHandleDeleteChild = hook.handleDeleteChild;
  const dashboardHandleInviteTeamMember = hook.handleInviteTeamMember;
  const dashboardHandleMessages = hook.handleMessages;
  const dashboardHandleShowCareReport = hook.handleShowCareReport;
  const importFileInputRef = useRef(null);
  const [careToastOpen, setCareToastOpen] = useState(false);
  const [careToastMessage, setCareToastMessage] = useState('');
  const [careToastSeverity, setCareToastSeverity] = useState('success');
  const [loggingReminderPromptOpen, setLoggingReminderPromptOpen] = useState(false);
  const [loggingReminderPromptLoading, setLoggingReminderPromptLoading] = useState(false);
  const [loggingReminderPromptChild, setLoggingReminderPromptChild] = useState(null);
  const [inboundEmailRequest, setInboundEmailRequest] = useState({ child: null, key: 0 });
  const [loggingReminderSettings, setLoggingReminderSettings] = useState(notificationSettingsDefaults);
  const [loggingReminderLoadingByChild, setLoggingReminderLoadingByChild] = useState({});
  const [quickComposerSaving, setQuickComposerSaving] = useState(false);
  const [quickComposerSavingMode, setQuickComposerSavingMode] = useState('note');
  const [pendingChildShellTarget, setPendingChildShellTarget] = useState('');
  const captureDetailsRequestRef = useRef({ key: '', requestKey: '' });
  const medicationOccurrenceRequestRef = useRef('');
  captureDetailsRequestRef.current = rememberOpenCaptureDetailsRequest(
    captureDetailsRequestRef.current,
    location.state,
    location.key
  );
  const captureDetailsRequest = captureDetailsRequestRef.current;
  const timelineNavigationState = useMemo(() => (
    captureDetailsRequest.key || location.state
      ? {
        ...(location.state || {}),
        openCaptureDetailsKey: captureDetailsRequest.key,
        openCaptureDetailsRequestKey: captureDetailsRequest.requestKey,
      }
      : null
  ), [captureDetailsRequest.key, captureDetailsRequest.requestKey, location.state]);
  const [importPickerConfig, setImportPickerConfig] = useState({
    accept: IMPORT_FILE_ACCEPT,
    sourceType: CARE_SOURCE_TYPES.FILE_IMPORT,
    title: 'Upload file',
    description: 'Upload a PDF, document, note, report, care plan, or medication instruction.',
    actionLabel: 'Choose file',
    captureDate: null,
    sourceUseCase: '',
    reviewPolicy: '',
  });
  const {
    showImportStartModal,
    importLoading,
    importLoadingContent,
    importError,
    importStatus,
    setImportError,
    setImportStatus,
    openImportStartModal,
    closeImportStartModal,
    startImportLoading,
    stopImportLoading,
    clearImportError,
    clearImportStatus,
  } = useImportReviewUiState();
  useMountDebug('PanelDashboard');
  trackRenderDebug('PanelDashboard', {
    isMobile,
    isDesktop,
    loading: hook.loading,
    childCount: hook.children.length,
    currentChildId: hook.currentChildId || 'none',
  });

  const handleImportLogsClick = useCallback((_child = null, event = null, options = {}) => {
    event?.stopPropagation?.();
    const isImageUpload = options.uploadKind === 'image';
    const isImportHistory = !options.uploadKind || options.importHistory === true;
    if (isImportHistory) {
      navigate('/import-history', {
        state: { selectedChildId: _child?.id || hook.currentChildId || null },
      });
      return;
    }
    setImportPickerConfig({
      accept: isImageUpload ? IMPORT_IMAGE_ACCEPT : IMPORT_FILE_ACCEPT,
      sourceType: options.sourceType || (
        isImageUpload
          ? CARE_SOURCE_TYPES.SCREENSHOT
          : CARE_SOURCE_TYPES.FILE_IMPORT
      ),
      title: isImageUpload ? 'Upload image' : 'Upload file',
      description: isImageUpload
        ? 'Upload a photo or screenshot to save as a care source.'
        : 'Upload a PDF, document, note, report, care plan, or medication instruction.',
      actionLabel: isImageUpload ? 'Choose image' : 'Choose file',
      captureDate: options.captureDate instanceof Date
        ? new Date(options.captureDate)
        : (hook.quickEntryDate instanceof Date ? new Date(hook.quickEntryDate) : null),
      sourceUseCase: '',
      reviewPolicy: '',
    });
    openImportStartModal();
  }, [hook.currentChildId, hook.quickEntryDate, navigate, openImportStartModal]);

  const { handleImportFileChange } = useImportFileReviewController({
    children: hook.children,
    currentChildId: hook.currentChildId,
    closeImportStartModal,
    startImportLoading,
    stopImportLoading,
    clearImportError,
    setImportError,
    setImportStatus,
    selectedSourceType: importPickerConfig.sourceType,
    selectedDate: importPickerConfig.captureDate,
    selectedLocalDate: getCalendarDateKey(importPickerConfig.captureDate),
    sourceUseCase: importPickerConfig.sourceUseCase,
    reviewPolicy: importPickerConfig.reviewPolicy,
    user: hook.user,
  });

  const handleLaunchImportPicker = useCallback(() => {
    if (importFileInputRef.current) {
      importFileInputRef.current.value = '';
      importFileInputRef.current.click();
    }
  }, []);

  useEffect(() => {
    let active = true;

    if (!hook.user) {
      setLoggingReminderSettings(notificationSettingsDefaults);
      return () => {
        active = false;
      };
    }

    loadUserNotificationSettings()
      .then((settings) => {
        if (active) {
          setLoggingReminderSettings(settings);
        }
      })
      .catch((error) => {
        console.error("Error loading notification settings:", error);
      });

    return () => {
      active = false;
    };
  }, [hook.user]);

  const captureTextAsBackgroundSource = useBackgroundCareCapture({
    setCareToastMessage,
    setCareToastSeverity,
    setCareToastOpen,
  });

  const maybeShowLoggingReminderPrompt = useCallback(async ({ child = null, isEdit = false } = {}) => {
    if (isEdit) {
      return;
    }

    try {
      const promptChild = child || dashboardSelectedChild || hook.selectedChild;
      const childId = promptChild?.id || '';
      if (!childId || isLoggingReminderEnabledForChild(loggingReminderSettings, childId)) {
        return;
      }

      const dismissedKey = `${LOGGING_REMINDER_PROMPT_DISMISSED_KEY}:${childId}`;
      const dismissedAt = Number(window.localStorage.getItem(dismissedKey) || 0);
      if (dismissedAt && Date.now() - dismissedAt < LOGGING_REMINDER_PROMPT_COOLDOWN_MS) {
        return;
      }

      setCareToastOpen(false);
      setLoggingReminderPromptChild(promptChild);
      setLoggingReminderPromptOpen(true);
    } catch (error) {
      console.error("Error checking logging reminder prompt:", error);
    }
  }, [dashboardSelectedChild, hook.selectedChild, loggingReminderSettings]);

  const composerSubmission = useComposerCaptureSubmit({
    captureSource: captureTextAsBackgroundSource,
    openQuickEntry: hook.handleQuickDataEntry,
    setSaving: setQuickComposerSaving,
    setSavingMode: setQuickComposerSavingMode,
    onFailure: (error) => {
      setCareToastMessage(error.message);
      setCareToastSeverity('error');
      setCareToastOpen(true);
    },
  });
  const handleMobileComposerSubmit = composerSubmission.submit;
  const handleComposerSkip = () => {
    composerSubmission.cancel();
    hook.handleQuickEntrySkip();
  };
  const handleComposerError = (error) => {
    composerSubmission.fail();
    setImportError(error);
  };

  const handleQuickEntryCompleteWithToast = useCallback((payload) => {
    composerSubmission.complete();
    setQuickComposerSaving(false);
    dashboardHandleQuickEntryComplete(payload);
    setCareToastMessage(payload?.toastMessage || 'Saved to timeline');
    setCareToastSeverity('success');
    setCareToastOpen(true);
    maybeShowLoggingReminderPrompt({
      child: payload?.child || hook.selectedChild || dashboardSelectedChild,
      isEdit: Boolean(payload?.isEditMode || payload?.entryId),
    });
  }, [composerSubmission.complete, dashboardHandleQuickEntryComplete, dashboardSelectedChild, hook.selectedChild, maybeShowLoggingReminderPrompt]);

  const handleDismissLoggingReminderPrompt = useCallback(() => {
    const childId = loggingReminderPromptChild?.id || dashboardSelectedChild?.id || hook.selectedChild?.id || 'unknown';
    window.localStorage.setItem(`${LOGGING_REMINDER_PROMPT_DISMISSED_KEY}:${childId}`, String(Date.now()));
    setLoggingReminderPromptOpen(false);
  }, [dashboardSelectedChild, hook.selectedChild, loggingReminderPromptChild]);

  const handleToggleLoggingReminderForChild = useCallback(async (child) => {
    const childId = child?.id || '';
    if (!childId) {
      return;
    }

    const currentlyEnabled = isLoggingReminderEnabledForChild(loggingReminderSettings, childId);
    const nextEnabled = !currentlyEnabled;
    setLoggingReminderLoadingByChild((current) => ({
      ...current,
      [childId]: true,
    }));

    try {
      let result = { success: true };
      if (nextEnabled) {
        result = await enableLoggingReminderNotifications(childId);
      } else {
        await saveLoggingReminderForChild(childId, false);
      }

      if (!result.success) {
        setCareToastMessage(getReminderErrorMessage(result.status));
        setCareToastSeverity('error');
        setCareToastOpen(true);
        return;
      }

      setLoggingReminderSettings((current) => ({
        ...current,
        loggingRemindersByChild: {
          ...(current.loggingRemindersByChild || {}),
          [childId]: nextEnabled,
        },
      }));
      setCareToastMessage(nextEnabled ? 'Daily reminder turned on' : 'Daily reminder turned off');
      setCareToastSeverity('success');
      setCareToastOpen(true);
    } catch (error) {
      console.error("Error changing logging reminder:", error);
      setCareToastMessage('Could not update reminder');
      setCareToastSeverity('error');
      setCareToastOpen(true);
    } finally {
      setLoggingReminderLoadingByChild((current) => {
        const next = { ...current };
        delete next[childId];
        return next;
      });
    }
  }, [loggingReminderSettings]);

  const handleEnableLoggingReminderPrompt = useCallback(async () => {
    const child = loggingReminderPromptChild || dashboardSelectedChild || hook.selectedChild;
    if (!child) {
      setLoggingReminderPromptOpen(false);
      return;
    }

    setLoggingReminderPromptLoading(true);
    try {
      const result = await enableLoggingReminderNotifications(child.id);
      setLoggingReminderPromptOpen(false);
      if (result.success) {
        setLoggingReminderSettings((current) => ({
          ...current,
          loggingRemindersByChild: {
            ...(current.loggingRemindersByChild || {}),
            [child.id]: true,
          },
        }));
      }
      setCareToastMessage(result.success ? 'Daily reminder turned on' : getReminderErrorMessage(result.status));
      setCareToastSeverity(result.success ? 'success' : 'error');
      setCareToastOpen(true);
    } catch (error) {
      console.error("Error enabling logging reminder:", error);
      setLoggingReminderPromptOpen(false);
      setCareToastMessage('Could not turn on reminders on this device');
      setCareToastSeverity('error');
      setCareToastOpen(true);
    } finally {
      setLoggingReminderPromptLoading(false);
    }
  }, [dashboardSelectedChild, hook.selectedChild, loggingReminderPromptChild]);

  const handleHeadlessQuickNoteConvert = useHeadlessQuickNoteConvert({
    composerSubmission, captureTextAsBackgroundSource, dashboardSelectedChild, hook,
    setQuickComposerSavingMode, setQuickComposerSaving,
    setCareToastMessage, setCareToastSeverity, setCareToastOpen,
  });

  const handleOpenMedicalLog = (child) => {
    if (!child) {
      return;
    }

    hook.handleOpenMedicalLog(child);
  };

  const handleCloseMedicalLog = () => {
    hook.handleCloseMedicalLog?.();
  };
  const medicalLogChild = hook.medicalLogChild || hook.children?.find((child) => child.id === hook.currentChildId) || null;

  const handleMoodSheetSave = useCallback(async (selectedMood) => {
    const child = hook.moodSheetChild || hook.selectedChild || dashboardSelectedChild || null;
    if (!child?.id || !selectedMood) {
      return;
    }

    await logMood(child.id, selectedMood);
    window.dispatchEvent(new CustomEvent('captureez:timeline-refresh', {
      detail: {
        childId: child.id,
      },
    }));
  }, [dashboardSelectedChild, hook.moodSheetChild, hook.selectedChild]);

  const handleMoodSheetClear = useCallback(async () => {
    const child = hook.moodSheetChild || hook.selectedChild || dashboardSelectedChild || null;
    if (!child?.id) {
      return;
    }

    await logMood(child.id, null);
    window.dispatchEvent(new CustomEvent('captureez:timeline-refresh', {
      detail: {
        childId: child.id,
      },
    }));
  }, [dashboardSelectedChild, hook.moodSheetChild, hook.selectedChild]);

  const handleTimelineNoteEditSaved = useCallback((updatedEntry) => {
    const child = hook.timelineNoteEditChild || dashboardSelectedChild || null;
    const patchedEntry = {
      ...(hook.timelineNoteEditEntry || {}),
      ...(updatedEntry || {}),
      childId: child?.id || updatedEntry?.childId || hook.timelineNoteEditEntry?.childId || null,
    };
    const previousDay = hook.timelineNoteEditEntry?.localDate || hook.timelineNoteEditEntry?.entryDayKey || null;
    const nextDay = patchedEntry.localDate || patchedEntry.entryDayKey || null;
    const eventName = previousDay && nextDay && previousDay !== nextDay
      ? 'captureez:timeline-refresh'
      : 'captureez:timeline-entry-updated';
    const detail = {
      childId: patchedEntry.childId,
      entryId: patchedEntry.id || null,
      collection: 'dailyLogs',
    };
    hook.handleTimelineNoteEditComplete?.(patchedEntry);
    hook.handleCloseTimelineNoteEditSheet?.();
    window.dispatchEvent(new CustomEvent(eventName, {
      detail: eventName === 'captureez:timeline-entry-updated'
        ? { ...detail, patch: patchedEntry }
        : detail,
    }));
    setCareToastMessage(updatedEntry?.refreshQueued ? 'Saved changes. Lifelog is refreshing this entry.' : 'Saved changes');
    setCareToastSeverity('success');
    setCareToastOpen(true);
  }, [dashboardSelectedChild, hook]);

  const moodSheetCurrentMood = getMoodOption(
    hook.moodSheetEntry?.moodValue
    || hook.moodSheetEntry?.value
    || hook.moodSheetEntry?.data?.level
    || hook.moodSheetEntry?.title
    || ''
  )?.label || null;

  const smartCaptureNoteSheetTitle = useMemo(() => {
    const category = String(hook.smartCaptureNoteEditEntry?.category || hook.smartCaptureNoteEditEntry?.type || '').trim().toLowerCase();
    if (category === 'sleep') {
      return 'Sleep note';
    }
    if (category === 'mood') {
      return 'Mood note';
    }
    return 'Note';
  }, [hook.smartCaptureNoteEditEntry]);

  useEffect(() => {
    const consumed = consumeOpenCaptureDetailsRequest(location.state);
    if (!consumed.captureDetailsKey) return;

    navigate(location.pathname, {
      replace: true,
      state: consumed.remainingState,
    });
  }, [location.pathname, location.state, navigate]);

  useEffect(() => {
    const target = location.state?.childShellTarget;
    const childId = location.state?.selectedChildId;
    if (!target || !dashboardChildren.length) {
      return;
    }

    if (childId && dashboardChildren.some((child) => child.id === childId)) {
      dashboardSetCurrentChildId(childId);
      window.dispatchEvent(new CustomEvent('captureez:set-active-child', {
        detail: { childId },
      }));
    }

    setPendingChildShellTarget(target);

    if (location.state?.timelineToastMessage) {
      setCareToastMessage(location.state.timelineToastMessage);
      setCareToastSeverity('success');
      setCareToastOpen(true);
    }

    const nextState = { ...(location.state || {}) };
    delete nextState.childShellTarget;
    delete nextState.selectedChildId;
    delete nextState.timelineToastMessage;

    navigate(location.pathname, {
      replace: true,
      state: Object.keys(nextState).length ? nextState : null,
    });
  }, [
    dashboardChildren,
    dashboardSetCurrentChildId,
    location.pathname,
    location.state,
    navigate,
  ]);

  useEffect(() => {
    if (!dashboardChildren.length) {
      return;
    }

    const consumed = consumeChildSelectionRequest(
      location.state,
      dashboardChildren.map((child) => child.id)
    );
    if (!consumed.hadRequest) {
      return;
    }

    if (consumed.requestedChildId) {
      dashboardSetCurrentChildId(consumed.requestedChildId);
      window.dispatchEvent(new CustomEvent('captureez:set-active-child', {
        detail: { childId: consumed.requestedChildId },
      }));
    }

    navigate(location.pathname, {
      replace: true,
      state: consumed.remainingState,
    });
  }, [
    dashboardChildren,
    dashboardSetCurrentChildId,
    location.pathname,
    location.state,
    navigate,
  ]);

  useEffect(() => {
    const requestedChildId = location.state?.openChildMedicationManager?.childId;
    if (!requestedChildId || !dashboardChildren.length) {
      return;
    }

    const requestedChild = dashboardChildren.find((child) => child.id === requestedChildId);
    if (!requestedChild) {
      return;
    }

    dashboardSetCurrentChildId(requestedChild.id);
    dashboardHandleEditChild(requestedChild, {
      initialStep: CARE_SNAPSHOT_STEPS.HEALTH_CARE,
      openMedicationManager: true,
    });

    navigate(location.pathname, { replace: true, state: null });
  }, [
    dashboardChildren,
    dashboardSetCurrentChildId,
    dashboardHandleEditChild,
    location.pathname,
    location.state,
    navigate,
  ]);

  useEffect(() => {
    const query = new URLSearchParams(location.search);
    const occurrenceId = query.get('medicationOccurrence') || '';
    const requestedChildId = query.get('childId') || '';
    if (!occurrenceId || !requestedChildId || medicationOccurrenceRequestRef.current === occurrenceId || !dashboardChildren.length) {
      return;
    }
    const requestedChild = dashboardChildren.find((child) => child.id === requestedChildId);
    if (!requestedChild) {
      return;
    }
    medicationOccurrenceRequestRef.current = occurrenceId;
    dashboardSetCurrentChildId(requestedChild.id);
    dashboardHandleEditChild(requestedChild, {
      initialStep: CARE_SNAPSHOT_STEPS.HEALTH_CARE,
      openMedicationManager: true,
    });
  }, [
    dashboardChildren,
    dashboardHandleEditChild,
    dashboardSetCurrentChildId,
    location.search,
  ]);

  useEffect(() => {
    const childId = location.state?.openImportedCalendarChildId;
    if (!childId || !dashboardChildren.length) {
      return;
    }

    const child = dashboardChildren.find((item) => item.id === childId);
    if (!child) {
      return;
    }

    dashboardSetCurrentChildId(child.id);
    requestAnimationFrame(() => {
      document.getElementById(`timeline-widget-${child.id}`)?.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      });
    });

    navigate(location.pathname, { replace: true, state: null });
  }, [
    dashboardChildren,
    dashboardSetCurrentChildId,
    location.pathname,
    location.state,
    navigate,
  ]);

  useEffect(() => {
    const childId = location.state?.openImportedPrepChildId;
    if (!childId || !dashboardChildren.length) {
      return;
    }

    const child = dashboardChildren.find((item) => item.id === childId);
    if (!child) {
      return;
    }

    hook.handleShowCareReport(child);
    navigate(location.pathname, { replace: true, state: null });
  }, [
    dashboardChildren,
    hook,
    location.pathname,
    location.state,
    navigate,
  ]);

  useEffect(() => {
    if (!location.state?.openQuickEntry || !dashboardChildren.length) {
      return;
    }

    const childId = location.state?.selectedChildId;
    const child = dashboardChildren.find((item) => item.id === childId) || dashboardSelectedChild || dashboardChildren[0] || null;
    if (child) {
      hook.handleQuickDataEntry(child, 'quick_note', null, null, location.state?.quickEntryInitialText || '');
    }

    navigate(location.pathname, { replace: true, state: null });
  }, [
    dashboardChildren,
    dashboardSelectedChild,
    hook,
    location.pathname,
    location.state,
    navigate,
  ]);

  useEffect(() => {
    const handleDashboardAction = (event) => {
      const action = event?.detail?.action;
      const childId = event?.detail?.childId;
      const child = dashboardChildren.find((item) => item.id === childId) || dashboardSelectedChild || dashboardChildren[0] || null;

      if (!action) {
        return;
      }

      switch (action) {
        case DASHBOARD_ACTION_IDS.ADD_CHILD:
          dashboardHandleAddChild();
          break;
        case DASHBOARD_ACTION_IDS.VIEW_CARE_TEAM:
          navigate('/care-team');
          break;
        case DASHBOARD_ACTION_IDS.INVITE_CAREGIVER:
          if (child) {
            dashboardHandleInviteTeamMember(child.id);
          }
          break;
        case DASHBOARD_ACTION_IDS.START_CHAT:
          if (child) {
            dashboardHandleMessages(child);
          }
          break;
        case DASHBOARD_ACTION_IDS.PREP_FOR_THERAPY:
          if (child) {
            dashboardHandleShowCareReport(child);
          }
          break;
        case DASHBOARD_ACTION_IDS.IMPORT_LOGS:
          handleImportLogsClick(child);
          break;
        case DASHBOARD_ACTION_IDS.EDIT_CHILD:
          if (child) {
            dashboardHandleEditChild(child);
          }
          break;
        case DASHBOARD_ACTION_IDS.DELETE_CHILD:
          if (child) {
            dashboardHandleDeleteChild(child);
          }
          break;
        default:
          break;
      }
    };

    window.addEventListener("captureez:dashboard-action", handleDashboardAction);

    return () => {
      window.removeEventListener("captureez:dashboard-action", handleDashboardAction);
    };
  }, [
    dashboardChildren,
    dashboardHandleDeleteChild,
    dashboardHandleEditChild,
    dashboardHandleInviteTeamMember,
    dashboardHandleMessages,
    dashboardHandleShowCareReport,
    dashboardSelectedChild,
    dashboardHandleAddChild,
    navigate,
    handleImportLogsClick,
  ]);

  useEffect(() => {
    const handleDailyCareSaved = (event) => {
      const message = event?.detail?.message;
      if (!message) {
        return;
      }

      setCareToastMessage(message);
      setCareToastSeverity('success');
      setCareToastOpen(true);
    };

    window.addEventListener('captureez:daily-care-saved', handleDailyCareSaved);
    window.addEventListener('captureez:capture-note-saved', handleDailyCareSaved);

    return () => {
      window.removeEventListener('captureez:daily-care-saved', handleDailyCareSaved);
      window.removeEventListener('captureez:capture-note-saved', handleDailyCareSaved);
    };
  }, []);

  if (hook.loading) {
    return (
      <Container maxWidth="xl" sx={{ mt: 4 }}>
        <Fade in={true}>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              minHeight: '60vh',
              gap: 3,
            }}
          >
            <Box
              sx={{
                position: 'relative',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <CircularProgress
                size={60}
                thickness={4}
                sx={{
                  color: colors.brand.ink,
                  '& .MuiCircularProgress-circle': {
                    strokeLinecap: 'round',
                  },
                }}
              />
              <Box
                sx={{
                  position: 'absolute',
                  fontSize: '2rem',
                  animation: 'pulse 2s ease-in-out infinite',
                  '@keyframes pulse': {
                    '0%, 100%': {
                      opacity: 0.7,
                      transform: 'scale(1)',
                    },
                    '50%': {
                      opacity: 1,
                      transform: 'scale(1.1)',
                    },
                  },
                }}
              >
                🏠
              </Box>
            </Box>
            <Typography
              variant="h6"
              sx={{
                color: 'text.secondary',
                fontWeight: 500,
                textAlign: 'center',
              }}
            >
              Loading your dashboard...
            </Typography>
            <Typography
              variant="body2"
              sx={{
                color: 'text.secondary',
                opacity: 0.7,
                textAlign: 'center',
                maxWidth: 300,
              }}
            >
              Getting your children's information and care updates ready
            </Typography>
          </Box>
        </Fade>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ mt: { xs: 2, md: 4 }, mb: { xs: 3, md: 4 } }}>
      <RenderDebugOverlay />
      {hook.children.length === 0 ? (
        <Box
          sx={{
            minHeight: '70vh',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            px: { xs: 0, sm: 2 },
          }}
        >
          <Box
            sx={{
              maxWidth: 940,
              width: '100%',
              bgcolor: 'background.paper',
              border: `1px solid ${colors.semantic.line}`,
              borderRadius: { xs: 3, sm: 4 },
              px: { xs: 2.5, sm: 5, md: 7 },
              py: { xs: 3.5, sm: 5, md: 6 },
              boxShadow: '0 28px 70px rgba(15, 23, 42, 0.08)',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.2, mb: { xs: 3, md: 4 } }}>
              <Box
                sx={{
                  width: 34,
                  height: 34,
                  borderRadius: '10px',
                  bgcolor: colors.semantic.sageDeep,
                  display: 'grid',
                  placeItems: 'center',
                }}
              >
                <Box sx={{ width: 10, height: 10, borderRadius: '4px', bgcolor: 'white' }} />
              </Box>
              <Typography sx={{ fontSize: '1.15rem', fontWeight: 900, color: colors.landing.textStrong }}>
                lifelog
              </Typography>
            </Box>

            <Typography
              sx={{
                color: colors.semantic.sageDeep,
                fontWeight: 900,
                letterSpacing: '0.06em',
                textTransform: 'uppercase',
                mb: 1.2,
              }}
            >
              Let's get started
            </Typography>
            <Typography
              sx={{
                fontWeight: 950,
                color: colors.landing.textStrong,
                mb: 2,
                fontSize: { xs: '2rem', sm: '2.35rem', md: '2.65rem' },
                lineHeight: 1.08,
              }}
            >
              Welcome to Lifelog
            </Typography>
            <Typography sx={{ maxWidth: 720, fontSize: { xs: '1.05rem', md: '1.22rem' }, lineHeight: 1.45, color: colors.semantic.ink, mb: { xs: 3, md: 4.5 } }}>
              One calm place for everyday care moments. To begin, create a profile for the person you care for, so every log, timeline, invite, and dashboard is clearly about <Box component="strong" sx={{ color: colors.landing.textStrong }}>them</Box>. It takes about a minute, and you only need three things.
            </Typography>

            <Box sx={{ display: 'grid', gap: { xs: 2, md: 0 }, mb: { xs: 3, md: 4 } }}>
              {[
                {
                  number: '1',
                  title: 'Their name',
                  text: "What you'd like to call them. It labels every log, timeline, invite, and dashboard so nothing gets mixed up between people.",
                },
                {
                  number: '2',
                  title: 'Age or date of birth',
                  tag: 'either works',
                  text: 'Helps Lifelog keep prompts and care context age-appropriate. The same note means something different for a toddler and for an elder.',
                },
                {
                  number: '3',
                  title: 'Care focus',
                  tag: 'the one that shapes everything',
                  text: "Pick the main reason you're tracking care. This is the key choice. It tailors the profile cards and prompts you'll see.",
                  choices: ['Autism', 'Dementia', 'Chronic illness'],
                  featured: true,
                },
              ].map((item) => (
                <Box
                  key={item.number}
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: { xs: '44px minmax(0, 1fr)', sm: '58px minmax(0, 1fr)' },
                    gap: { xs: 1.5, sm: 2 },
                    alignItems: 'flex-start',
                    py: item.featured ? { xs: 2, sm: 2.4 } : { xs: 1.5, sm: 2 },
                    px: item.featured ? { xs: 1.5, sm: 2 } : 0,
                    borderTop: item.number === '1' || item.featured ? 0 : `1px solid ${colors.semantic.line}`,
                    border: item.featured ? `1px solid ${colors.semantic.line}` : 'none',
                    borderRadius: item.featured ? 2.5 : 0,
                    bgcolor: item.featured ? colors.landing.sageLight : 'transparent',
                  }}
                >
                  <Box
                    sx={{
                      width: 42,
                      height: 42,
                      borderRadius: '50%',
                      bgcolor: item.featured ? colors.semantic.sageSoft : colors.semantic.sageMist,
                      color: colors.semantic.sageDeep,
                      display: 'grid',
                      placeItems: 'center',
                      fontWeight: 950,
                      fontSize: '1rem',
                    }}
                  >
                    {item.number}
                  </Box>
                  <Box sx={{ minWidth: 0 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mb: 0.5 }}>
                      <Typography sx={{ fontSize: { xs: '1.05rem', md: '1.18rem' }, fontWeight: 950, color: colors.landing.textStrong }}>
                        {item.title}
                      </Typography>
                      {item.tag ? (
                        <Box
                          component="span"
                          sx={{
                            px: 1,
                            py: 0.2,
                            borderRadius: 999,
                            bgcolor: colors.semantic.sageMist,
                            color: colors.semantic.sageDeep,
                            fontSize: '0.72rem',
                            fontWeight: 900,
                          }}
                        >
                          {item.tag}
                        </Box>
                      ) : null}
                    </Box>
                    <Typography sx={{ fontSize: { xs: '0.96rem', md: '1.05rem' }, lineHeight: 1.45, color: colors.semantic.ink }}>
                      {item.text}
                    </Typography>
                    {item.choices ? (
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1.6 }}>
                        {item.choices.map((choice) => (
                          <Box
                            key={choice}
                            sx={{
                              px: 1.25,
                              py: 0.65,
                              borderRadius: 999,
                              border: `1px solid ${colors.semantic.line2}`,
                              bgcolor: colors.semantic.bg,
                              color: colors.landing.textStrong,
                              fontWeight: 850,
                              fontSize: '0.88rem',
                            }}
                          >
                            {choice}
                          </Box>
                        ))}
                      </Box>
                    ) : null}
                  </Box>
                </Box>
              ))}
            </Box>

            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: 'auto minmax(0, 1fr)',
                gap: 1.4,
                alignItems: 'flex-start',
                bgcolor: colors.semantic.sageMist,
                borderRadius: 2.5,
                px: { xs: 1.6, sm: 2 },
                py: { xs: 1.5, sm: 1.7 },
                mb: { xs: 3, md: 4 },
              }}
            >
              <Box sx={{ width: 30, height: 30, borderRadius: '50%', bgcolor: colors.semantic.sageDeep, color: 'white', display: 'grid', placeItems: 'center', mt: 0.1 }}>
                <CheckRoundedIcon sx={{ fontSize: 20 }} />
              </Box>
              <Typography sx={{ fontSize: { xs: '0.98rem', md: '1.05rem' }, lineHeight: 1.45, color: colors.semantic.ink }}>
                <Box component="strong" sx={{ color: colors.landing.textStrong }}>That's all you need to begin.</Box>{" "}
                Start with just these three. Photos, medications, routines, and your care team can all be added later, whenever you're ready. Nothing else is required up front.
              </Typography>
            </Box>

            <Box sx={{ display: 'flex', alignItems: { xs: 'stretch', sm: 'center' }, gap: { xs: 1.5, sm: 2.5 }, flexDirection: { xs: 'column', sm: 'row' } }}>
            <Button
              variant="contained"
              size="large"
              onClick={() => hook.handleAddChild()}
              startIcon={<AddRoundedIcon />}
              sx={{
                px: 3.2,
                py: 1.4,
                fontSize: '1.05rem',
                fontWeight: 900,
                borderRadius: 999,
                textTransform: 'none',
                bgcolor: colors.semantic.sageDeep,
                boxShadow: `0 14px 28px ${colors.landing.shadowHero}`,
                '&:hover': {
                  bgcolor: colors.semantic.sage,
                  boxShadow: `0 16px 32px ${colors.landing.shadowHero}`,
                },
              }}
            >
              Add Profile
            </Button>
              <Typography sx={{ fontSize: '0.98rem', color: colors.semantic.ink2, textAlign: { xs: 'center', sm: 'left' } }}>
                Takes about a minute. Add more anytime.
              </Typography>
            </Box>
          </Box>
        </Box>
      ) : (
        <DashboardViewProvider
          childOptions={hook.children}
          initialActiveChildId={hook.currentChildId || hook.children[0]?.id || null}
          onActiveChildChange={hook.setCurrentChildId}
        >
          {isMobile ? (
              <MobileDashboardFlow
                children={hook.children}
                getUserRoleForChild={hook.getUserRoleForChild}
                USER_ROLES={hook.USER_ROLES}
                allEntries={hook.allEntries}
                recentEntries={hook.recentEntries}
                timelineSummary={hook.timelineSummary}
                incidents={hook.incidents}
                timelineNavigationState={timelineNavigationState}
                onRefreshDashboard={hook.refreshDashboard}
              onQuickEntry={hook.handleQuickDataEntry}
              onComposerSubmit={handleMobileComposerSubmit}
              composerSaving={quickComposerSaving}
              composerSavingLabel={quickComposerSavingMode === 'source' ? 'Saving source...' : 'Saving...'}
              composerSavingHelper={quickComposerSavingMode === 'source'
                ? "Saving the original source. We'll organize care details in the background."
                : 'Saving your note and organizing care details...'}
              onConvertQuickNoteToEntries={handleHeadlessQuickNoteConvert}
              onEditChild={hook.handleEditChild}
              onOpenChildReview={hook.handleReviewChild}
              onDeleteChild={hook.handleDeleteChild}
              onInviteTeamMember={hook.handleInviteTeamMember}
              onDailyReport={hook.handleShowCareReport}
              onTrack={hook.handleTrack}
              onOpenSleepLog={hook.handleOpenSleepLog}
              onOpenFoodLog={hook.handleOpenFoodLog}
              onOpenBathroomLog={hook.handleOpenBathroomLog}
              onOpenMedicalLog={handleOpenMedicalLog}
              onOpenIncident={(child) => hook.handleQuickDataEntry(child, 'incident')}
              onMessages={hook.handleMessages}
              onImportLogs={handleImportLogsClick}
              onAddChildClick={() => hook.handleAddChild()}
              onRefreshRoles={hook.refreshRoles}
              loggingRemindersByChild={loggingReminderSettings.loggingRemindersByChild}
              loggingReminderLoadingByChild={loggingReminderLoadingByChild}
              onToggleLoggingReminder={handleToggleLoggingReminderForChild}
              childShellTarget={pendingChildShellTarget}
              initialOpenCaptureDetailsKey={captureDetailsRequest.key}
              initialOpenCaptureRequestKey={captureDetailsRequest.requestKey}
              onChildShellTargetHandled={() => setPendingChildShellTarget('')}
              showSleepLogSheet={
                hook.showSleepLogSheet
                || hook.showFoodLogSheet
                || hook.showBathroomLogSheet
                || hook.showDailyCareModal
                || hook.showDailyHabitsModal
                || hook.showQuickEntry
                || hook.showIncidentModal
                || hook.showFollowUpModal
                || hook.showPatternSuggestionModal
                || hook.showFoodLogSheet
                || hook.showMedicalLogPanel
              }
              showFoodLogSheet={hook.showFoodLogSheet}
              showBathroomLogSheet={hook.showBathroomLogSheet}
            />
          ) : (
            <DesktopDashboardWorkspace
              hook={hook}
              onQuickEntry={hook.handleQuickDataEntry}
              onComposerSubmit={handleMobileComposerSubmit}
              composerSaving={quickComposerSaving}
              composerSavingLabel={quickComposerSavingMode === 'source' ? 'Saving source...' : 'Saving...'}
              composerSavingHelper={quickComposerSavingMode === 'source'
                ? "Saving the original source. We'll organize care details in the background."
                : 'Saving your note and organizing care details...'}
              onConvertQuickNoteToEntries={handleHeadlessQuickNoteConvert}
              onDailyReport={hook.handleShowCareReport}
              onOpenSleepLog={hook.handleOpenSleepLog}
              onOpenFoodLog={hook.handleOpenFoodLog}
              onOpenBathroomLog={hook.handleOpenBathroomLog}
              onOpenMedicalLog={handleOpenMedicalLog}
              onImportLogs={handleImportLogsClick}
              onGoToCareTeam={(child) => navigate('/care-team', {
                state: {
                  childId: child?.id || hook.currentChildId || null,
                  focusChildId: child?.id || hook.currentChildId || null,
                },
              })}
              onAddChildClick={() => hook.handleAddChild()}
              loggingRemindersByChild={loggingReminderSettings.loggingRemindersByChild}
              loggingReminderLoadingByChild={loggingReminderLoadingByChild}
              onToggleLoggingReminder={handleToggleLoggingReminderForChild}
              timelineNavigationState={timelineNavigationState}
            />
          )}
        </DashboardViewProvider>
      )}

      <input
        ref={importFileInputRef}
        type="file"
        accept={importPickerConfig.accept}
        onChange={handleImportFileChange}
        style={{ display: 'none' }}
      />

      <ProfileInboundEmailCard
        key={`inbound-email-${inboundEmailRequest.key}`}
        childId={(inboundEmailRequest.child || dashboardSelectedChild || hook.selectedChild)?.id || ''}
        personName={(inboundEmailRequest.child || dashboardSelectedChild || hook.selectedChild)?.name || ''}
        canManage={['care_owner', 'care_partner'].includes(
          hook.getUserRoleForChild?.((inboundEmailRequest.child || dashboardSelectedChild || hook.selectedChild)?.id)
        )}
        openOnLoad={Boolean(inboundEmailRequest.child)}
        showLauncher={false}
        onClose={() => setInboundEmailRequest((current) => ({ child: null, key: current.key }))}
      />

      {/* Modals */}
      {hook.showQuickEntry && hook.quickEntryAutoSubmit && hook.selectedChild && hook.entryType === "full" ? (
        <QuickCheckIn
          child={hook.selectedChild}
          onComplete={handleQuickEntryCompleteWithToast}
          onSkip={handleComposerSkip}
          onConvertToEntries={handleHeadlessQuickNoteConvert}
          onAiCaptureStart={startImportLoading}
          onAiCaptureEnd={stopImportLoading}
          onAiCaptureError={handleComposerError}
          open={hook.showQuickEntry}
          initialDate={hook.quickEntryDate}
          initialPhotoFile={hook.quickEntryInitialPhotoFile}
          initialPhotoIntent={hook.quickEntryInitialPhotoIntent}
          initialText={hook.quickEntryInitialText}
          autoSubmitOnOpen
          headless
          embedded
          showHeader={false}
          showMetaFields={false}
          showCapturePreviewSection={false}
          showTagSection={false}
          showDebugDetails={false}
        />
      ) : null}

      {!hook.quickEntryAutoSubmit ? (
        <NaturalCaptureComposerShell
          open={hook.showQuickEntry}
          onClose={handleComposerSkip}
          title="Add care note"
          eyebrow="NEW ENTRY"
          subtitle="From today or the past - type, talk, or import."
          child={hook.selectedChild}
          hideHeader
          bodySx={{
            display: 'flex',
            flexDirection: 'column',
            px: { xs: 0, sm: 0, md: 0 },
            pt: 0,
            pb: 0,
          }}
        >
          {hook.selectedChild && hook.entryType === "micro" && (
            <Box sx={{ px: 2, py: 2 }}>
              <div>Micro data collection not available</div>
            </Box>
          )}
          {hook.selectedChild && hook.entryType === "full" && (
            <QuickCheckIn
              child={hook.selectedChild}
              onComplete={handleQuickEntryCompleteWithToast}
              onSkip={handleComposerSkip}
              onConvertToEntries={handleHeadlessQuickNoteConvert}
              onAiCaptureStart={startImportLoading}
              onAiCaptureEnd={stopImportLoading}
              onAiCaptureError={handleComposerError}
              open={hook.showQuickEntry}
              initialStep={hook.quickEntryStep}
              initialDate={hook.quickEntryDate}
              initialAction={hook.quickEntryInitialAction}
              initialPhotoFile={hook.quickEntryInitialPhotoFile}
              initialText={hook.quickEntryInitialText}
              onCaptureDateChange={(nextDate) => {
                hook.setQuickEntryDate?.(nextDate);
                if (nextDate instanceof Date && !Number.isNaN(nextDate.getTime())) {
                  window.dispatchEvent(new CustomEvent('captureez:quick-entry-date-change', {
                    detail: { date: nextDate },
                  }));
                }
              }}
              onImportLogs={handleImportLogsClick}
              onForwardEmail={['care_owner', 'care_partner'].includes(
                hook.getUserRoleForChild?.(hook.selectedChild?.id)
              ) ? (child) => setInboundEmailRequest((current) => ({
                child,
                key: current.key + 1,
              })) : null}
              onOpenSleepLog={hook.handleOpenSleepLog}
              onOpenFoodLog={hook.handleOpenFoodLog}
              onOpenMedicalLog={handleOpenMedicalLog}
              onOpenBathroomLog={hook.handleOpenBathroomLog}
              onOpenIncident={(child) => hook.handleQuickDataEntry(child, 'incident')}
              embedded
              showHeader={false}
              showMetaFields={false}
              showCapturePreviewSection={false}
              showTagSection={false}
              showDebugDetails={false}
            />
          )}
        </NaturalCaptureComposerShell>
      ) : null}

      {hook.smartCaptureNoteChild && hook.smartCaptureNoteEditEntry && (
          <NaturalCaptureComposerShell
          open={hook.showSmartCaptureNoteSheet}
          onClose={hook.handleCloseSmartCaptureNoteSheet}
          title="Log update"
          eyebrow={hook.smartCaptureNoteEditEntry ? 'EDIT ENTRY' : 'NEW ENTRY'}
          subtitle="Capture anything from today. We’ll organize it for review."
          child={hook.smartCaptureNoteChild || hook.selectedChild || dashboardSelectedChild || null}
        >
          <QuickCheckIn
            child={hook.smartCaptureNoteChild}
            onComplete={hook.handleCloseSmartCaptureNoteSheet}
            onSkip={hook.handleCloseSmartCaptureNoteSheet}
            open={hook.showSmartCaptureNoteSheet}
            mode="edit"
            initialData={hook.smartCaptureNoteEditEntry}
            entryId={hook.smartCaptureNoteEditEntry?.id || null}
            sheetTitle={smartCaptureNoteSheetTitle}
            showCapturePreviewSection={false}
            showTagSection={false}
            showDebugDetails={false}
            showMetaFields={false}
            overrideCategory={hook.smartCaptureNoteEditEntry?.category || hook.smartCaptureNoteEditEntry?.type || null}
            embedded
            showHeader={false}
          />
        </NaturalCaptureComposerShell>
      )}


      <TimelineNoteEditSheet
        open={hook.showTimelineNoteEditSheet}
        child={hook.timelineNoteEditChild}
        entry={hook.timelineNoteEditEntry}
        currentUserId={hook.user?.uid || null}
        onClose={hook.handleCloseTimelineNoteEditSheet}
        onSaved={handleTimelineNoteEditSaved}
      />

      <DailyCareModal
        open={hook.showDailyCareModal}
        onClose={hook.handleCloseDailyCareModal}
        child={hook.dailyCareChild}
        actionType={hook.dailyCareAction}
        initialData={hook.dailyCareEditEntry}
        entryId={hook.dailyCareEditEntry?.id || null}
        mode={hook.dailyCareEditEntry ? 'edit' : 'create'}
      />

      <LogMoodModal
        open={hook.showMoodSheet}
        onClose={hook.handleCloseMoodSheet}
        child={hook.moodSheetChild || hook.selectedChild || dashboardSelectedChild || null}
        currentMood={moodSheetCurrentMood}
        onSave={handleMoodSheetSave}
        onClearMood={hook.moodSheetEntry?.id ? handleMoodSheetClear : null}
      />

      <SleepLogSheet
        open={hook.showSleepLogSheet}
        onClose={hook.handleCloseSleepLogSheet}
        child={hook.sleepLogChild}
        initialData={hook.sleepLogEditEntry}
        mode={hook.sleepLogEditEntry ? 'edit' : 'create'}
        sleepType={hook.sleepLogType}
      />

      <FoodLogSheet
        open={hook.showFoodLogSheet}
        onClose={hook.handleCloseFoodLogSheet}
        child={hook.foodLogChild}
        initialData={hook.foodLogEditEntry}
        mode={hook.foodLogEditEntry ? 'edit' : 'create'}
      />

      <BathroomLogSheet
        open={hook.showBathroomLogSheet}
        onClose={hook.handleCloseBathroomLogSheet}
        child={hook.bathroomLogChild}
        initialData={hook.bathroomLogEditEntry}
        mode={hook.bathroomLogEditEntry ? 'edit' : 'create'}
      />

      <IncidentLoggingModal
        open={hook.showIncidentModal}
        onClose={hook.handleCloseIncidentModal}
        child={hook.incidentChild}
        childId={hook.incidentChild?.id}
        childName={hook.incidentChild?.name}
        initialData={hook.incidentEditEntry}
        mode={hook.incidentEditEntry ? 'edit' : 'create'}
      />

      <IncidentFollowUpModal
        open={hook.showFollowUpModal}
        onClose={hook.handleCloseFollowUpModal}
        incident={hook.followUpIncident}
        childName={hook.children.find(c => c.id === hook.followUpIncident?.childId)?.name}
      />

      <PatternSuggestionModal
        open={hook.showPatternSuggestionModal}
        onClose={hook.handleClosePatternSuggestionModal}
        suggestions={hook.patternSuggestions}
        childName={hook.children.find(c => c.id === hook.suggestionsChildId)?.name}
        onCreateCategory={hook.handleCreateCustomCategories}
      />

      <DailyHabitsModal
        open={hook.showDailyHabitsModal}
        onClose={hook.handleCloseDailyHabitsModal}
        childId={hook.dailyHabitsChild?.id}
        childName={hook.dailyHabitsChild?.name}
        initialCategoryId={hook.dailyHabitsInitialCategoryId}
        initialData={hook.dailyHabitsEditEntry}
        mode={hook.dailyHabitsEditEntry ? 'edit' : 'create'}
      />

      <ImportReviewOverlays
        showImportStartModal={showImportStartModal}
        onCloseImportStartModal={closeImportStartModal}
        onLaunchImportPicker={handleLaunchImportPicker}
        importStartTitle={importPickerConfig.title}
        importStartDescription={importPickerConfig.description}
        importStartHelper="CaptureEase saves the original source first, then organizes timeline updates and care knowledge in the background."
        importStartActionLabel={importPickerConfig.actionLabel}
        importLoading={importLoading}
        importLoadingContent={importLoadingContent}
        importError={importError}
        importStatus={importStatus}
        onClearImportError={clearImportError}
        onClearImportStatus={clearImportStatus}
        isMobile={isMobile}
      />

      <BulkMedicationLogDialog
        open={hook.showMedicalLogPanel}
        childId={medicalLogChild?.id || hook.currentChildId}
        childName={medicalLogChild?.name || ''}
        child={medicalLogChild}
        onClose={handleCloseMedicalLog}
        editEntry={hook.medicalLogEditEntry}
      />

      <Snackbar
        open={careToastOpen}
        autoHideDuration={6000}
        onClose={() => setCareToastOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setCareToastOpen(false)}
          severity={careToastSeverity}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {careToastMessage}
        </Alert>
      </Snackbar>

      <Snackbar
        open={loggingReminderPromptOpen}
        onClose={handleDismissLoggingReminderPrompt}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={handleDismissLoggingReminderPrompt}
          severity="info"
          variant="filled"
          sx={{ width: '100%', alignItems: 'center' }}
          action={
            <Box sx={{ display: 'flex', gap: 0.75 }}>
              <Button
                color="inherit"
                size="small"
                onClick={handleDismissLoggingReminderPrompt}
                sx={{ textTransform: 'none', fontWeight: 800 }}
              >
                Not now
              </Button>
              <Button
                color="inherit"
                size="small"
                onClick={handleEnableLoggingReminderPrompt}
                disabled={loggingReminderPromptLoading}
                sx={{ textTransform: 'none', fontWeight: 900 }}
              >
                {loggingReminderPromptLoading ? 'Turning on...' : 'Turn on'}
              </Button>
            </Box>
          }
        >
          Want a gentle reminder to log for {loggingReminderPromptChild?.name || 'this profile'}?
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default PanelDashboard;
